var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import './vitesse-icon';
/*
 * <vitesse-pill-tabs> — the filter pill bar with a SLIDING active indicator.
 *
 * LIGHT DOM (the global .pill-tab-* SCSS in styles/components/_base-pill-tab-bar
 * styles it). One absolutely-positioned .pill-tab-indicator carries the active
 * blue gradient and SLIDES — fast quadratic ease — from the old pill to the new
 * on selection, "shooting across" the pills in between; the inactive pills are
 * transparent so the sweep stays visible (the indicator, not a background bar,
 * is the only thing that moves). Re-measures on resize + content change; the
 * first paint + resizes snap instantly (only a user-driven selection animates).
 */
let VitessePillTabs = class VitessePillTabs extends LitElement {
    constructor() {
        super(...arguments);
        this.tabs = [];
        this.active = '';
        this.positioned = false;
    }
    // Light DOM so the shared global pill SCSS applies (matches the rest of base-ui).
    createRenderRoot() {
        return this;
    }
    connectedCallback() {
        super.connectedCallback();
        this.ro = new ResizeObserver(() => this.positionIndicator(false));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.ro?.disconnect();
    }
    firstUpdated() {
        const bar = this.querySelector('.pill-tab-bar');
        if (bar)
            this.ro?.observe(bar); // track wraps / width changes
        this.positionIndicator(false); // snap to the active pill, no slide on first paint
    }
    updated() {
        this.positionIndicator(true); // a selection (or count/width change) → slide
    }
    /** Clip the indicator to the UNION of the pill shapes, so as it slides between
     *  pills the blue only shows OVER pills — never in the inter-pill gaps. Recomputed
     *  whenever pill geometry can change (firstUpdate / selection / resize). */
    syncClip(bar) {
        const clip = this.querySelector('.pill-tab-clip');
        if (!clip)
            return;
        const barRect = bar.getBoundingClientRect();
        const R = 6; // matches the pill border-radius
        let d = '';
        for (const p of this.querySelectorAll('.pill-tab')) {
            const pr = p.getBoundingClientRect();
            const x = pr.left - barRect.left;
            const y = pr.top - barRect.top;
            const w = pr.width;
            const h = pr.height;
            const r = Math.min(R, w / 2, h / 2);
            // One rounded-rect subpath per pill; disjoint subpaths union into the clip.
            d +=
                `M${x + r},${y}h${w - 2 * r}a${r},${r} 0 0 1 ${r},${r}v${h - 2 * r}` +
                    `a${r},${r} 0 0 1 ${-r},${r}h${-(w - 2 * r)}a${r},${r} 0 0 1 ${-r},${-r}` +
                    `v${-(h - 2 * r)}a${r},${r} 0 0 1 ${r},${-r}Z`;
        }
        clip.style.clipPath = d ? `path('${d}')` : '';
    }
    /** Park the indicator over the active pill. `animate` false snaps instantly
     *  (first paint, resize); true lets the CSS quadratic transition slide it. */
    positionIndicator(animate) {
        const bar = this.querySelector('.pill-tab-bar');
        const indicator = this.querySelector('.pill-tab-indicator');
        const activeBtn = this.querySelector('.pill-tab.active');
        if (!bar || !indicator)
            return;
        this.syncClip(bar); // keep the pill-shape clip current before moving the indicator
        if (!activeBtn) {
            indicator.style.opacity = '0'; // nothing selected → hide it
            return;
        }
        const barRect = bar.getBoundingClientRect();
        const r = activeBtn.getBoundingClientRect();
        const x = r.left - barRect.left;
        const y = r.top - barRect.top; // non-zero when the bar wraps to a 2nd line
        const place = () => {
            indicator.style.width = `${r.width}px`;
            indicator.style.height = `${r.height}px`;
            indicator.style.transform = `translate(${x}px, ${y}px)`;
            indicator.style.opacity = '1';
        };
        if (animate && this.positioned) {
            place();
            return;
        }
        // Snap: kill the transition for this frame so it appears/tracks instantly.
        const prev = indicator.style.transition;
        indicator.style.transition = 'none';
        place();
        void indicator.offsetWidth; // force reflow so the snap commits before restore
        indicator.style.transition = prev;
        this.positioned = true;
    }
    select(t) {
        if (t.disabled || t.id === this.active)
            return;
        this.onChange?.(t.id);
    }
    render() {
        return html `
      <div class="pill-tab-bar">
        <div class="pill-tab-clip">
          <div class="pill-tab-indicator"></div>
        </div>
        ${this.tabs.map((t) => html `
            <button
              class="pill-tab ${t.id === this.active ? 'active' : ''}"
              ?disabled=${t.disabled}
              title=${t.title ?? nothing}
              @click=${() => this.select(t)}
              type="button"
            >
              ${t.icon ? html `<span class="pill-tab-icon"><vitesse-icon name=${t.icon} size="14"></vitesse-icon></span>` : nothing}
              <span class="pill-tab-label">${t.label}</span>
              ${t.status ? html `<span class="pill-tab-dot pill-tab-dot-${t.status}" aria-hidden="true"></span>` : nothing}
              ${t.count !== undefined && t.count > 0 ? html `<span class="pill-tab-count">${t.count}</span>` : nothing}
            </button>
          `)}
      </div>
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitessePillTabs.prototype, "tabs", void 0);
__decorate([
    property()
], VitessePillTabs.prototype, "active", void 0);
__decorate([
    property({ attribute: false })
], VitessePillTabs.prototype, "onChange", void 0);
VitessePillTabs = __decorate([
    customElement('vitesse-pill-tabs')
], VitessePillTabs);
export { VitessePillTabs };
//# sourceMappingURL=vitesse-pill-tabs.js.map